import React, { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  DataTableSkeleton,
  EmptyState,
  PageHeader,
  ScheduleCalendar,
  SectionCard,
} from "components/admin";
import { DataTable } from "components/molecules/table/table";
import { buildPlayerCalendarItems } from "pages/app/workspace/sections/helpers/workspace-calendar.helpers";
import { workspaceApi } from "pages/app/workspace/services";
import {
  PlayerStatsSectionGridLayout,
  PlayerStatsSectionGridLayoutSecondary,
  PlayerStatsSectionSection,
} from "pages/app/workspace/sections/styles/player-stats-section.inline.styles";
export const PlayerStatsSection: React.FC<{
  currentUser: any;
}> = ({ currentUser }) => {
  const playerId = currentUser?.player?.id;
  const careersQuery = useQuery({
    queryKey: ["workspace-player-careers", playerId],
    queryFn: () => workspaceApi.athletes.getCareers(playerId),
    enabled: Boolean(playerId),
  });
  const seasonStatsQuery = useQuery({
    queryKey: ["workspace-player-season-stats", playerId],
    queryFn: () => workspaceApi.athletes.getSeasonStats(playerId),
    enabled: Boolean(playerId),
  });
  const positionsQuery = useQuery({
    queryKey: ["workspace-player-positions", playerId],
    queryFn: () => workspaceApi.athletes.getPositionsPlayed(playerId),
    enabled: Boolean(playerId),
  });
  const achievementsQuery = useQuery({
    queryKey: ["workspace-player-achievements", playerId],
    queryFn: () => workspaceApi.athletes.getAchievements(playerId),
    enabled: Boolean(playerId),
  });
  const calendarQuery = useQuery({
    queryKey: ["workspace-player-calendar", playerId],
    queryFn: () => workspaceApi.athletes.getCalendarEvents(playerId),
    enabled: Boolean(playerId),
  });
  const playerCalendarItems = useMemo(
    () => buildPlayerCalendarItems(calendarQuery.data || []),
    [calendarQuery.data],
  );
  if (!playerId) {
    return (
      <>
        <PageHeader
          title="My Stats"
          description="This section becomes available when the current user has a linked athlete record."
        />
        <EmptyState
          description="The current account does not have a linked player record yet."
          title="No linked athlete stats"
        />
      </>
    );
  }
  return (
    <>
      <PageHeader
        title="My Stats"
        description="Review your careers, season totals, positions, achievements, and calendar events as returned by the updated player endpoints."
      />

      <PlayerStatsSectionGridLayout>
        <SectionCard subtitle="Career timeline" title="Careers">
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Team",
                accessor: (row: any) => row.team_name || "Unknown",
              },
              {
                label: "League",
                accessor: (row: any) => row.league_name || "Unknown",
              },
              {
                label: "Season",
                accessor: (row: any) =>
                  `${row.start_year || "?"}${row.end_year ? ` - ${row.end_year}` : ""}`,
              },
              {
                label: "Type",
                accessor: (row: any) => row.contract_type || "Unknown",
              },
            ]}
            data={careersQuery.data || []}
            hideNav
            isLoading={careersQuery.isLoading}
            loadingState={<DataTableSkeleton columns={4} rows={4} />}
          />
        </SectionCard>

        <SectionCard subtitle="Season output" title="Season Stats">
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Season",
                accessor: (row: any) => row.season || "Unknown",
              },
              {
                label: "Tournament",
                accessor: (row: any) => row.tournament || "Unknown",
              },
              {
                label: "Matches",
                accessor: (row: any) => row.matches_played ?? 0,
              },
              {
                label: "Points",
                accessor: (row: any) => row.points ?? 0,
              },
            ]}
            data={seasonStatsQuery.data || []}
            hideNav
            isLoading={seasonStatsQuery.isLoading}
            loadingState={<DataTableSkeleton columns={4} rows={4} />}
          />
        </SectionCard>
      </PlayerStatsSectionGridLayout>

      <PlayerStatsSectionGridLayoutSecondary>
        <SectionCard subtitle="Positions played across seasons" title="Positions">
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Position",
                accessor: (row: any) => row.position || "Unknown",
              },
              {
                label: "Appearances",
                accessor: (row: any) => row.appearances ?? 0,
              },
              {
                label: "Share",
                accessor: (row: any) => `${row.percentage || 0}%`,
              },
              {
                label: "Season",
                accessor: (row: any) => row.season || "Unknown",
              },
            ]}
            data={positionsQuery.data || []}
            hideNav
            isLoading={positionsQuery.isLoading}
            loadingState={<DataTableSkeleton columns={4} rows={4} />}
          />
        </SectionCard>

        <SectionCard subtitle="Awards and honours" title="Achievements">
          <DataTable
            canEdit={false}
            canRemove={false}
            columns={[
              {
                label: "Title",
                accessor: (row: any) => row.title || "Unknown",
              },
              {
                label: "Category",
                accessor: (row: any) => row.category || "General",
              },
              {
                label: "Season",
                accessor: (row: any) => row.season || row.year || "Unknown",
              },
              {
                label: "Tournament",
                accessor: (row: any) => row.tournament || "Unknown",
              },
            ]}
            data={achievementsQuery.data || []}
            hideNav
            isLoading={achievementsQuery.isLoading}
            loadingState={<DataTableSkeleton columns={4} rows={4} />}
          />
        </SectionCard>
      </PlayerStatsSectionGridLayoutSecondary>

      <PlayerStatsSectionSection>
        <SectionCard subtitle="Upcoming athlete events" title="Calendar Events">
          <ScheduleCalendar
            emptyDescription="Upcoming athlete events will appear here once they have been created."
            emptyTitle="No athlete events"
            isLoading={calendarQuery.isLoading}
            items={playerCalendarItems}
          />
        </SectionCard>
      </PlayerStatsSectionSection>
    </>
  );
};
